import React, { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PlusIcon, Trash2Icon, ImageIcon, CheckCircleIcon } from 'lucide-react';
interface Option {
  id: string;
  name: string;
  values: string;
}
interface Modifier {
  id: string;
  name: string;
  price: number;
}
export function ProductEdit() {
  const navigate = useNavigate();
  const {
    productId
  } = useParams<{
    productId: string;
  }>();
  const [params] = useSearchParams();
  const shopId = params.get('shop') || '';
  const isNew = productId === 'new';
  const [name, setName] = useState(isNew ? '' : 'Swim Shorts');
  const [description, setDescription] = useState(isNew ? '' : 'Quick-dry swim shorts for men, made from recycled polyester.');
  const [sku, setSku] = useState(isNew ? '' : 'SS-003');
  const [category, setCategory] = useState(isNew ? 'Swimwear' : 'Swimwear');
  const [price, setPrice] = useState(isNew ? '0.00' : '34.99');
  const [salePrice, setSalePrice] = useState(isNew ? '' : '29.99');
  const [stockQuantity, setStockQuantity] = useState(isNew ? '0' : '28');
  const [imageUrl, setImageUrl] = useState(isNew ? '' : 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80');
  const [isActive, setIsActive] = useState(true);
  const [options, setOptions] = useState<Option[]>(isNew ? [] : [{
    id: 'o1',
    name: 'Size',
    values: 'S, M, L, XL'
  }, {
    id: 'o2',
    name: 'Color',
    values: 'Navy, Coral, Black'
  }]);
  const [modifiers, setModifiers] = useState<Modifier[]>(isNew ? [] : [{
    id: 'm1',
    name: 'Express delivery',
    price: 5.0
  }]);
  const [savedToast, setSavedToast] = useState(false);
  const addOption = () => setOptions([...options, {
    id: 'o' + Math.random().toString(36).slice(2, 7),
    name: '',
    values: ''
  }]);
  const removeOption = (id: string) => setOptions(options.filter(o => o.id !== id));
  const addModifier = () => setModifiers([...modifiers, {
    id: 'm' + Math.random().toString(36).slice(2, 7),
    name: '',
    price: 0
  }]);
  const removeModifier = (id: string) => setModifiers(modifiers.filter(m => m.id !== id));
  const handleSave = () => {
    setSavedToast(true);
    setTimeout(() => {
      setSavedToast(false);
      navigate(shopId ? `/shop-details?id=${shopId}` : '/shops');
    }, 1200);
  };
  return <div className="max-w-[1200px] mx-auto space-y-6" data-id="element-12277">
      <div data-id="element-12278">
        <button onClick={() => navigate(shopId ? `/shop-details?id=${shopId}` : '/shops')} className="inline-flex items-center text-sm text-gray-500 hover:text-gray-900 mb-3" data-id="element-12279">
          <ArrowLeftIcon size={14} className="mr-1.5" data-id="element-12280" />
          Back to shop
        </button>
        <div className="flex items-center justify-between" data-id="element-12281">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-12282">
            {isNew ? 'New product' : 'Edit product'}
          </h1>
          <div className="flex items-center gap-2" data-id="element-12283">
            <button onClick={() => navigate(-1)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50" data-id="element-12284">
              Cancel
            </button>
            <button onClick={handleSave} className="px-4 py-2 bg-[#E2231A] hover:bg-[#C41B14] text-white text-sm font-medium rounded-lg shadow-sm" data-id="element-12285">
              Save product
            </button>
          </div>
        </div>
      </div>

      {savedToast && <div className="fixed bottom-6 right-6 bg-gray-900 text-white px-4 py-2.5 rounded-lg text-sm shadow-lg flex items-center gap-2 z-50" data-id="element-12286">
          <CheckCircleIcon size={16} className="text-green-400" data-id="element-12287" />
          Product saved
        </div>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-12288">
        {/* Left column */}
        <div className="lg:col-span-2 space-y-6" data-id="element-12289">
          {/* Basic info */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12290">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12291">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-12292">
                Basic information
              </h2>
            </div>
            <div className="px-6 py-5 space-y-4" data-id="element-12293">
              <div data-id="element-12294">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12295">
                  Name <span className="text-red-400" data-id="element-12296">*</span>
                </label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12297" />
              </div>
              <div data-id="element-12298">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12299">
                  Description
                </label>
                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12300" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-12301">
                <div data-id="element-12302">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12303">
                    SKU
                  </label>
                  <input type="text" value={sku} onChange={e => setSku(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12304" />
                </div>
                <div data-id="element-12305">
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12306">
                    Category
                  </label>
                  <input type="text" value={category} onChange={e => setCategory(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12307" />
                </div>
              </div>
            </div>
          </div>

          {/* Pricing & inventory */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12308">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12309">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-12310">
                Pricing & inventory
              </h2>
            </div>
            <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-12311">
              <div data-id="element-12312">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12313">
                  Regular price (MYR) <span className="text-red-400" data-id="element-12314">*</span>
                </label>
                <input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12315" />
              </div>
              <div data-id="element-12316">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12317">
                  Sale price (MYR)
                </label>
                <input type="number" step="0.01" value={salePrice} onChange={e => setSalePrice(e.target.value)} placeholder="Optional" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12318" />
                <p className="text-xs text-gray-400 mt-1" data-id="element-12319">
                  Leave blank for no discount
                </p>
              </div>
              <div data-id="element-12320">
                <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12321">
                  Stock quantity
                </label>
                <input type="number" value={stockQuantity} onChange={e => setStockQuantity(e.target.value)} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12322" />
              </div>
            </div>
          </div>

          {/* Product options */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12323">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-12324">
              <div data-id="element-12325">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12326">
                  Product options
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-12327">
                  Variants the customer must pick from, e.g. Size or Color.
                </p>
              </div>
              <button onClick={addOption} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-12328">
                <PlusIcon size={14} data-id="element-12329" />
                Add option
              </button>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-12330">
              {options.map((opt, i) => <div key={opt.id} className="border border-gray-200 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[1fr_2fr_auto] gap-3 items-end" data-id="element-12331">
                  <div data-id="element-12332">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-12333">
                      Option name
                    </label>
                    <input type="text" value={opt.name} onChange={e => setOptions(options.map(o => o.id === opt.id ? {
                  ...o,
                  name: e.target.value
                } : o))} placeholder="e.g. Size" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12334" />
                  </div>
                  <div data-id="element-12335">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-12336">
                      Values (comma separated)
                    </label>
                    <input type="text" value={opt.values} onChange={e => setOptions(options.map(o => o.id === opt.id ? {
                  ...o,
                  values: e.target.value
                } : o))} placeholder="e.g. S, M, L, XL" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12337" />
                  </div>
                  <button onClick={() => removeOption(opt.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" aria-label="Remove option" data-id="element-12338">
                    <Trash2Icon size={16} data-id="element-12339" />
                  </button>
                </div>)}
              {options.length === 0 && <p className="text-sm text-gray-500 text-center py-3" data-id="element-12340">
                  No options yet — add one to let customers pick a variant.
                </p>}
            </div>
          </div>

          {/* Modifiers */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12341">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" data-id="element-12342">
              <div data-id="element-12343">
                <h2 className="text-sm font-semibold text-gray-900" data-id="element-12344">
                  Modifiers
                </h2>
                <p className="text-xs text-gray-500 mt-0.5" data-id="element-12345">
                  Optional add-ons with their own price (e.g. gift wrap,
                  monogram).
                </p>
              </div>
              <button onClick={addModifier} className="inline-flex items-center gap-1.5 text-sm text-[#E2231A] hover:text-[#C41B14] font-medium" data-id="element-12346">
                <PlusIcon size={14} data-id="element-12347" />
                Add modifier
              </button>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-12348">
              {modifiers.map(m => <div key={m.id} className="border border-gray-200 rounded-lg p-3 grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto] gap-3 items-end" data-id="element-12349">
                  <div data-id="element-12350">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-12351">
                      Modifier name
                    </label>
                    <input type="text" value={m.name} onChange={e => setModifiers(modifiers.map(x => x.id === m.id ? {
                  ...x,
                  name: e.target.value
                } : x))} placeholder="e.g. Gift wrap" className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12352" />
                  </div>
                  <div data-id="element-12353">
                    <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-12354">
                      Extra price (MYR)
                    </label>
                    <input type="number" step="0.01" value={m.price} onChange={e => setModifiers(modifiers.map(x => x.id === m.id ? {
                  ...x,
                  price: Number(e.target.value)
                } : x))} className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12355" />
                  </div>
                  <button onClick={() => removeModifier(m.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md" aria-label="Remove modifier" data-id="element-12356">
                    <Trash2Icon size={16} data-id="element-12357" />
                  </button>
                </div>)}
              {modifiers.length === 0 && <p className="text-sm text-gray-500 text-center py-3" data-id="element-12358">
                  No modifiers yet — add one to offer paid extras.
                </p>}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-6" data-id="element-12359">
          {/* Image */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12360">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2" data-id="element-12361">
              <ImageIcon size={16} className="text-gray-500" data-id="element-12362" />
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-12363">
                Product image
              </h2>
            </div>
            <div className="px-6 py-5 space-y-3" data-id="element-12364">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border border-gray-200" data-id="element-12365">
                {imageUrl ? <img src={imageUrl} alt="Preview" className="w-full h-full object-cover" data-id="element-12366" /> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-12367">
                    <ImageIcon size={32} data-id="element-12368" />
                  </div>}
              </div>
              <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1.5" data-id="element-12369">
                Image URL
              </label>
              <input type="url" value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A]" data-id="element-12370" />
            </div>
          </div>

          {/* Visibility */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-id="element-12371">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-12372">
              <h2 className="text-sm font-semibold text-gray-900" data-id="element-12373">
                Visibility
              </h2>
            </div>
            <div className="px-6 py-5" data-id="element-12374">
              <label className="flex items-center cursor-pointer" data-id="element-12375">
                <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="sr-only" data-id="element-12376" />
                <div className={`relative w-9 h-5 rounded-full transition-colors ${isActive ? 'bg-[#E2231A]' : 'bg-gray-200'}`} data-id="element-12377">
                  <div className={`absolute left-0.5 top-0.5 bg-white w-4 h-4 rounded-full transition-transform ${isActive ? 'translate-x-4' : ''}`} data-id="element-12378" />
                </div>
                <span className="ml-3 text-sm text-gray-700" data-id="element-12379">
                  {isActive ? 'Active — visible in shop' : 'Inactive — hidden from shop'}
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>;
}